import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import klydoApiClient, {
  Klydo,
  KlydoSearchFilter,
  KlydoType,
} from "../../api/klydo";
import { useEffect, useRef, useState } from "react";

export type SearchType = "TEXT" | "SIMILARITY";
export type DisplayType = "LIST" | "GRID";

const PAGE_SIZE_SIMILARITY = 20;

const useSealedKlydos = (
  query: string,
  limit: number,
  searchType: SearchType = "TEXT",
  filters?: KlydoSearchFilter,
  includeMeteData?: boolean,
  sortField?: string,
  sortDirection?: number,
  imageBase64?: string,
  useGeminiEmbedding?: boolean,
  klydoType?: KlydoType,
) => {
  const queryClient = useQueryClient();
  const dataRef = useRef<Klydo[]>();
  const [totalCount, setTotalCount] = useState<number | undefined>();

  const imageKey = imageBase64
    ? `img:${imageBase64.length}:${imageBase64.slice(0, 32)}`
    : undefined;

  const { data, fetchNextPage, hasNextPage, ...rest } = useInfiniteQuery<
    Klydo[],
    Error
  >({
    queryKey: [
      "klydos",
      query,
      searchType,
      filters?.unlisted,
      filters?.scheduled,
      filters?.isPublic,
      filters?.greet,
      filters?.inCollection,
      filters?.hasCustomChime,
      filters?.generatedByAI,
      filters?.ratingStage,
      filters?.tags,
      filters?.tagsMatchMode,
      limit,
      sortField,
      sortDirection,
      imageKey,
      useGeminiEmbedding,
      klydoType,
    ],
    queryFn: async ({ pageParam }) => {
      let sortOptions = undefined;
      if (sortField && sortDirection) {
        sortOptions = { [sortField]: sortDirection };
      }

      if (searchType === "SIMILARITY" && imageBase64) {
        const klydos = await klydoApiClient.similaritySearchByImage(
          imageBase64,
          {
            ...(filters || {}),
            excludeIds:
              (pageParam as { excludeIds: string[] })?.excludeIds || [],
          },
          PAGE_SIZE_SIMILARITY,
          useGeminiEmbedding,
        );
        klydos.forEach((klydo) => {
          queryClient.setQueryData(["klydo", klydo.id], klydo);
        });
        return klydos;
      }

      if (searchType === "SIMILARITY") {
        const klydos = await klydoApiClient.similaritySearch(
          query,
          {
            ...(filters || {}),
            excludeIds:
              (pageParam as { excludeIds: string[] })?.excludeIds || [],
          },
          PAGE_SIZE_SIMILARITY,
          useGeminiEmbedding,
        );
        klydos.forEach((klydo) => {
          queryClient.setQueryData(["klydo", klydo.id], klydo);
        });
        return klydos;
      }

      const skip = (pageParam as { skip?: number })?.skip || 0;

      const response = await klydoApiClient.textSearch(
        query,
        limit,
        skip,
        includeMeteData || false,
        filters || {},
        sortOptions,
        klydoType,
      );

      setTotalCount(response.total);

      response.data.forEach((klydo) => {
        queryClient.setQueryData(["klydo", klydo.id], klydo);
      });

      return response.data;
    },
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.length === 0) return undefined;

      if (searchType === "TEXT") {
        const currentLength = allPages.flat().length;
        return currentLength < limit ? undefined : { skip: currentLength };
      } else {
        if (lastPage.length < PAGE_SIZE_SIMILARITY) return undefined;
        const previous = allPages.flat();
        return { excludeIds: previous.map((klydo) => klydo.id) };
      }
    },
    initialPageParam: searchType === "TEXT" ? { skip: 0 } : { excludeIds: [] },
  });

  useEffect(() => {
    if (searchType === "SIMILARITY") {
      setTotalCount(undefined);
    }
  }, [searchType]);

  const flatData = data?.pages.flat();

  useEffect(() => {
    dataRef.current = flatData;
  }, [flatData]);

  return {
    data: flatData,
    dataRef,
    fetchNextPage,
    hasNextPage,
    totalCount,
    ...rest,
  };
};

export default useSealedKlydos;
